import type { GridFilterInputValueProps, GridFilterOperator, GridColDef, GridComparatorFn } from '@repo/go-table';
import { TableCell } from '@repo/go-table';
import { Select } from '@repo/go-ui';
import { format, fromUnixTime, getUnixTime, millisecondsToSeconds } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { TDictObject } from 'src/api/directories/types';
import { StatusLabel } from 'src/features/status-label';
import { defaultStatusData } from 'src/features/status-label/status-label';
import i18n from 'src/i18n';
import type { TheWellEntity } from 'src/services/wells-service/entities';
import type { TTheWellData } from 'src/services/wells-service/entities/the-well.entity';
import { capitalize } from 'src/shared/utils/capitalize';
import { hasValue } from 'src/shared/utils/common';
import { getNumberWithDecimalPlaces } from 'src/shared/utils/get-number-with-decimal-places';
import { useStore } from 'src/store';
import type { TPreparationStatus } from 'src/store/well-pad-preparation/types';
import { getReadinessStatusByDates } from 'src/store/well-pad-preparation/utils';

import type { WellsTableStore } from '../wells-table.store';

export const MIN_COLUMN_WIDTH = 80;

const statusOperator: GridFilterOperator<TheWellEntity, TPreparationStatus> = {
  label: i18n.t('wells:Table.contains'),
  value: 'contains',
  getApplyFilterFn: (filterItem) => {
    if (!filterItem.field || !filterItem.value || !filterItem.operator) {
      return null;
    }

    return (value) => {
      if (!filterItem.value.length) {
        return true;
      }

      return filterItem.value.includes(value);
    };
  },
  InputComponent: (props: GridFilterInputValueProps) => {
    const { t } = useTranslation();
    const { item, applyValue } = props;

    const handleFilterChange = (newValue: string[]) => {
      applyValue({ ...item, value: newValue });
    };

    return (
      <Select
        value={item.value}
        style={{ width: '100%' }}
        mode="multiple"
        options={Object.entries(defaultStatusData).map(([key, value]) => ({ label: value, value: key }))}
        placeholder={t('common:chooseValue')}
        onChange={handleFilterChange}
      />
    );
  },
};

const statusComparator: GridComparatorFn<TPreparationStatus> = (_v1, _v2) => {
  const getStatusAsNumber = (status: TPreparationStatus) => {
    switch (status) {
      case 'ready':
        return 0;
      case 'inProcess':
        return 1;
      case 'notReady':
        return 2;
    }
  };

  const v1 = getStatusAsNumber(_v1);
  const v2 = getStatusAsNumber(_v2);

  return v1 - v2;
};

export const useGetTableColumns = (store: WellsTableStore): GridColDef<TheWellEntity>[] => {
  const { t } = useTranslation();
  const { directories } = useStore();

  const {
    blockOptions,
    fieldOptions,
    organizationOptions,
    padOptions,
    fluidTypeOptions,
    wellTypeOptions,
    coreSamplingIntervalOptions,
    clusterOptions,
    reservoirOptions,
    drillingStatusOptions,
    wellPurposeOptions,
    wellEndingTypeOptions,
    wellCategoryOptions,
    rigOptions,
    brigadeOptions,
    padTypeOptions,
    passageTypeOptions,
    directoriesObjects,
    rows,
    getClusterIdFn,
  } = store;

  const baseColumn: Partial<GridColDef<TheWellEntity>> = useMemo(
    () => ({
      minWidth: MIN_COLUMN_WIDTH,
      editable: false,
      headerAlign: 'left',
      getApplyQuickFilterFn: () => null,
      valueGetter: (_$, row, column, api) => {
        const id = api.current.getRowId(row);
        const theWell = store.getRecordById(id as string);

        if (!theWell) {
          console.error('cannot find corresponding record');
          return undefined;
        }

        const value = theWell.data[column.field as keyof TTheWellData];

        if (!hasValue(value)) {
          return undefined;
        }

        switch (column.type) {
          case 'date': {
            return fromUnixTime(value as number);
          }

          case 'number': {
            if (!hasValue(value) || typeof value !== 'number') {
              return null;
            }

            return getNumberWithDecimalPlaces(value, 2);
          }

          default: {
            return value;
          }
        }
      },
      renderCell: (params) => {
        return <TableCell>{params.formattedValue ?? params.value}</TableCell>;
      },
    }),
    [store],
  );

  return useMemo(() => {
    const drillingStatuses = directories.getObject('Common_DrillingStatus') ?? [];
    const { drilled, inProgress, planned } = drillingStatuses.reduce(
      (acc: { drilled: number | null; inProgress: number | null; planned: number | null }, dict) => {
        if (dict.data['slug'] === 'drilled') {
          acc.drilled = dict.id as number;
        } else if (dict.data['slug'] === 'in-progress') {
          acc.inProgress = dict.id as number;
        } else if (dict.data['slug'] === 'planned') {
          acc.planned = dict.id as number;
        }

        return acc;
      },
      { drilled: null, inProgress: null, planned: null },
    );
    const stageDictObjects = directories.getObject('GOplan_Dict_WellLifeCycleStage') ?? [];
    const drillingStageId = stageDictObjects.find((dict) => dict.data['slug'] === 'drilling')?.id ?? null;

    const blockOptionsMap = new Map(blockOptions.map((option) => [option.value, option]));
    const fieldOptionsMap = new Map(fieldOptions.map((option) => [option.value, option]));
    const organizationOptionsMap = new Map(organizationOptions.map((option) => [option.value, option]));
    const padOptionsMap = new Map(padOptions.map((option) => [option.value, option]));
    const wellPurposeOptionsMap = new Map(wellPurposeOptions.map((option) => [option.value, option]));
    const wellEndingTypeOptionsMap = new Map(wellEndingTypeOptions.map((option) => [option.value, option]));
    const drillingStatusOptionsMap = new Map(drillingStatusOptions.map((option) => [option.value, option]));
    const clusterOptionsMap = new Map(clusterOptions.map((option) => [option.value, option]));
    const fluidTypeOptionsMap = new Map(fluidTypeOptions.map((option) => [option.value, option]));
    const wellTypeOptionsMap = new Map(wellTypeOptions.map((option) => [option.value, option]));
    const wellCategoryOptionsMap = new Map(wellCategoryOptions.map((option) => [option.value, option]));
    const rigOptionsMap = new Map(rigOptions.map((option) => [option.value, option]));
    const brigadeOptionsMap = new Map(brigadeOptions.map((option) => [option.value, option]));
    const padTypeOptionsMap = new Map(padTypeOptions.map((option) => [option.value, option]));
    const passageTypeOptionsMap = new Map(passageTypeOptions.map((option) => [option.value, option]));
    const reservoirOptionsMap = new Map(reservoirOptions.map((option) => [option.value, option]));
    const coreSamplingIntervalOptionsMap = new Map(coreSamplingIntervalOptions.map((option) => [option.value, option]));

    const columns: GridColDef<TheWellEntity>[] = [
      {
        ...baseColumn,
        type: 'string',
        field: 'wellName',
        headerName: t('wells:Labels.wellName'),
        // Костыль, чтобы вынесенные фильтры таблички работали максимально приближено к нативной реализации
        getApplyQuickFilterFn: (_value: string) => {
          const { startDate, endDate, isDrilledWellsChecked, isPlannedWellsChecked, isUnplannedWellsChecked } = (() => {
            try {
              const data = JSON.parse(_value) as {
                startDate: number | null;
                endDate: number | null;
                isDrilledWellsChecked: boolean;
                isPlannedWellsChecked: boolean;
                isUnplannedWellsChecked: boolean;
              };

              return data;
            } catch (e) {
              console.error(e);
              return {
                startDate: null,
                endDate: null,
                isDrilledWellsChecked: true,
                isPlannedWellsChecked: true,
                isUnplannedWellsChecked: true,
              };
            }
          })();

          return (_, row, _column, api) => {
            const id = api.current.getRowId(row);
            const theWell = store.getRecordById(id as string);

            if (!theWell) {
              console.error('cannot find corresponding record', id);
              return true;
            }

            if (!hasValue(drillingStageId)) {
              return true;
            }

            const today = getUnixTime(new Date());

            const drillingStage = theWell.data.stages?.find((stage) => stage.wellLifeCycleStageId === drillingStageId);
            const isSatisfyingDrilledFilter = (() => {
              if (isDrilledWellsChecked) {
                if (!drillingStage) {
                  return false;
                }

                if (theWell.data.drillingStatusId && theWell.data.drillingStatusId === drilled) {
                  return true;
                }

                if (drillingStage.endDate && drillingStage.endDate < today) {
                  return true;
                }
              }

              return false;
            })();
            const isSatisfyingPlannedFilter = (() => {
              if (isPlannedWellsChecked) {
                if (!drillingStage) {
                  return false;
                }

                if (
                  theWell.data.drillingStatusId &&
                  (theWell.data.drillingStatusId === planned || theWell.data.drillingStatusId === inProgress)
                ) {
                  return true;
                }

                if (drillingStage.endDate && drillingStage.endDate >= today) {
                  return true;
                }
              }

              return false;
            })();
            const isSatisfyingUnplannedFilter = (() => {
              if (isUnplannedWellsChecked) {
                if (!drillingStage) {
                  return true;
                }
              }

              return false;
            })();

            const isSatisfyingDateIntervalFilter = (() => {
              if (isSatisfyingUnplannedFilter || (!startDate && !endDate)) {
                return true;
              }

              if (!drillingStage) {
                return false;
              }

              if (startDate && endDate) {
                return (
                  !!drillingStage.startDate &&
                  drillingStage.startDate >= startDate &&
                  !!drillingStage.endDate &&
                  drillingStage.endDate <= endDate
                );
              }

              if (startDate && !!drillingStage.startDate && drillingStage.startDate >= startDate) {
                return true;
              }

              if (endDate && !!drillingStage.endDate && drillingStage.endDate <= endDate) {
                return true;
              }

              return false;
            })();

            const isSatisfyingFilters =
              isSatisfyingDrilledFilter || isSatisfyingPlannedFilter || isSatisfyingUnplannedFilter;

            return isSatisfyingFilters && isSatisfyingDateIntervalFilter;
          };
        },
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'blockId',
        headerName: t('wells:Labels.blockId'),
        valueOptions: blockOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const blockId = theWell.data['blockId'];

          if (!hasValue(blockId)) {
            return null;
          }

          return blockOptionsMap.get(blockId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'fieldId',
        headerName: t('wells:Labels.fieldId'),
        valueOptions: fieldOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const fieldId = theWell.data['fieldId'];

          if (!hasValue(fieldId)) {
            return null;
          }

          return fieldOptionsMap.get(fieldId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'clusterId',
        headerName: t('wells:Labels.clusterId'),
        valueOptions: clusterOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          if (!hasValue(theWell.data['blockId'])) {
            return null;
          }

          const clusterId = getClusterIdFn(theWell.data['blockId'] as number);

          if (!hasValue(clusterId)) {
            return null;
          }

          return clusterOptionsMap.get(clusterId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'subsoilUserId',
        headerName: t('wells:Labels.subsoilUserId'),
        valueOptions: organizationOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const subsoilUserId = theWell.data['subsoilUserId'];

          if (!hasValue(subsoilUserId)) {
            return null;
          }

          return organizationOptionsMap.get(subsoilUserId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'padId',
        headerName: t('wells:Labels.padId'),
        valueOptions: padOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const padId = theWell.data['padId'];

          if (!hasValue(padId)) {
            return null;
          }

          return padOptionsMap.get(padId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'wellNumber',
        headerName: t('wells:Labels.wellNumber'),
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'geoTaskIndex',
        headerName: t('wells:Labels.geoTaskIndex'),
        valueGetter: (value, row, column, api) => {
          const baseValue = baseColumn.valueGetter?.(value, row, column, api);

          if (!hasValue(baseValue) || typeof baseValue !== 'number') {
            return null;
          }

          return baseValue + 1;
        },
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'index',
        headerName: t('wells:Labels.index'),
        valueGetter: (value, row, column, api) => {
          const baseValue = baseColumn.valueGetter?.(value, row, column, api);

          if (!hasValue(baseValue) || typeof baseValue !== 'number') {
            return null;
          }

          return baseValue + 1;
        },
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'coordinatesT2X',
        headerName: t('wells:Labels.coordinatesT2X'),
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'coordinatesT2Y',
        headerName: t('wells:Labels.coordinatesT2Y'),
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'coordinatesT3X',
        headerName: t('wells:Labels.coordinatesT3X'),
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'coordinatesT3Y',
        headerName: t('wells:Labels.coordinatesT3Y'),
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'passingValue',
        headerName: t('wells:Labels.passingValue'),
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'trunkLength',
        headerName: t('wells:Labels.trunkLength'),
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'commercialSpeed',
        headerName: t('wells:Labels.commercialSpeed'),
        valueGetter: (value, row, column, api) => {
          const baseValue = baseColumn.valueGetter?.(value, row, column, api);
          if (!hasValue(baseValue) || typeof baseValue !== 'number') {
            return null;
          }

          return getNumberWithDecimalPlaces(baseValue, 0);
        },
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'speedPerDay',
        headerName: t('wells:Labels.speedPerDay'),
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'wellPurposeId',
        headerName: t('wells:Labels.wellPurposeId'),
        valueOptions: wellPurposeOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const wellPurposeId = theWell.data['wellPurposeId'];

          if (!hasValue(wellPurposeId)) {
            return null;
          }

          return wellPurposeOptionsMap.get(wellPurposeId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'fluidTypeId',
        headerName: t('wells:Labels.fluidTypeId'),
        valueOptions: fluidTypeOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const fluidTypeId = theWell.data['fluidTypeId'];

          if (!hasValue(fluidTypeId)) {
            return null;
          }

          return fluidTypeOptionsMap.get(fluidTypeId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'reservoirIds',
        headerName: t('wells:Labels.reservoirIds'),
        valueFormatter: (_value, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const values = theWell.data['reservoirIds'];

          if (!hasValue(values) || !values.length) {
            return null;
          }

          return (
            values
              .map((v) => {
                const correspondingOption = reservoirOptionsMap.get(v);

                return correspondingOption?.label ?? v;
              })
              .join(', ') ?? null
          );
        },
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'wellTypeId',
        headerName: t('wells:Labels.wellTypeId'),
        valueOptions: wellTypeOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const wellTypeId = theWell.data['wellTypeId'];

          if (!hasValue(wellTypeId)) {
            return null;
          }

          return wellTypeOptionsMap.get(wellTypeId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'wellCategoryId',
        valueOptions: wellCategoryOptions,
        headerName: t('wells:Labels.wellCategoryId'),
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const wellCategoryId = theWell.data['wellCategoryId'];

          if (!hasValue(wellCategoryId)) {
            return null;
          }

          return wellCategoryOptionsMap.get(wellCategoryId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'pilotNumber',
        headerName: t('wells:Labels.pilotNumber'),
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'wellEndingTypeId',
        headerName: t('wells:Labels.wellEndingTypeId'),
        valueOptions: wellEndingTypeOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const wellEndingTypeId = theWell.data['wellEndingTypeId'];

          if (!hasValue(wellEndingTypeId)) {
            return null;
          }

          return wellEndingTypeOptionsMap.get(wellEndingTypeId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'drillingStatusId',
        headerName: t('wells:Labels.drillingStatusId'),
        valueOptions: drillingStatusOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const drillingStatusId = theWell.data['drillingStatusId'];

          if (!hasValue(drillingStatusId)) {
            return null;
          }

          return drillingStatusOptionsMap.get(drillingStatusId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'additionalNote',
        headerName: t('wells:Labels.additionalNote'),
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'rigId',
        headerName: t('wells:Labels.rigId'),
        valueOptions: rigOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const rigId = theWell.data['rigId'];

          if (!hasValue(rigId)) {
            return null;
          }

          return rigOptionsMap.get(rigId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'rigNumber',
        headerName: t('wells:Labels.rigNumber'),
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'brigadeId',
        headerName: t('wells:Labels.brigadeId'),
        valueOptions: brigadeOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const brigadeId = theWell.data['brigadeId'];

          if (!hasValue(brigadeId)) {
            return null;
          }

          return brigadeOptionsMap.get(brigadeId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'maxH2s',
        headerName: t('wells:Labels.maxH2s'),
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'isSliderUsed',
        headerName: t('wells:Labels.isSliderUsed'),
        valueFormatter: (value) => {
          return value ? i18n.t('common:installed') : '';
        },
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'foc178',
        headerName: t('wells:Labels.foc178'),
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'foc245',
        headerName: t('wells:Labels.foc245'),
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'coreSamplingIntervalId',
        minWidth: 156,
        headerName: t('wells:Labels.coreSamplingIntervalId'),
        valueFormatter: (_value, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const values = theWell.data['coreSamplingIntervalId'];

          if (!hasValue(values) || !values.length) {
            return null;
          }

          return (
            values
              .map((v) => {
                const correspondingOption = coreSamplingIntervalOptionsMap.get(v);

                return correspondingOption?.label ?? v;
              })
              .join(', ') ?? null
          );
        },
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'extractionVolume',
        headerName: t('wells:Labels.extractionVolume'),
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'testingFormationWorkAmount',
        headerName: t('wells:Labels.testingFormationWorkAmount'),
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'numberOfDataInterpretationOperations',
        headerName: t('wells:Labels.numberOfDataInterpretationOperations'),
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'daysAmountOfDataInterpretationOperation',
        headerName: t('wells:Labels.daysAmountOfDataInterpretationOperation'),
      },
    ];

    const { stageColumns, sectionColumns } = (() => {
      const stageIdsSet = new Set<number>();
      const sectionIdsSet = new Set<number>();

      rows.forEach((row) => {
        const { stagesByStageType, sectionsBySectionType } = row.data;

        if (!stagesByStageType || !sectionsBySectionType) {
          return;
        }

        Object.keys(stagesByStageType).forEach((stageId) => {
          stageIdsSet.add(Number(stageId));
        });

        Object.keys(sectionsBySectionType).forEach((sectionId) => {
          sectionIdsSet.add(Number(sectionId));
        });
      });

      const stageColumns: GridColDef<TheWellEntity>[] = [];
      const sectionColumns: GridColDef<TheWellEntity>[] = [];

      const wellLifeCycleStagesMap = new Map<number, TDictObject>(
        directoriesObjects.wellLifeCycleStages.map((dict) => [Number(dict.id), dict]),
      );

      const stageIds = Array.from(stageIdsSet).sort((a, b) => {
        const stageDictionary1Order = wellLifeCycleStagesMap.get(a)?.data['order'] as number | undefined;
        const stageDictionary2Order = wellLifeCycleStagesMap.get(b)?.data['order'] as number | undefined;

        return (stageDictionary1Order ?? Infinity) - (stageDictionary2Order ?? Infinity);
      });

      stageIds.forEach((stageId) => {
        const stageDictionary = wellLifeCycleStagesMap.get(stageId);

        const hasDistance = stageDictionary?.data['hasDistance'] === true;

        stageColumns.push(
          {
            ...baseColumn,
            field: `stage_${stageId}_startDate`,
            headerName: t('common:startDate'),
            type: 'date',
            valueGetter: (_, row, _column, api) => {
              const id = api.current.getRowId(row);
              const theWell = store.getRecordById(id as string);

              if (!theWell) {
                console.error('cannot find corresponding record');
                return undefined;
              }

              const stagesByStageType = theWell.data['stagesByStageType'];

              if (!stagesByStageType) {
                return undefined;
              }

              const stage = stagesByStageType[stageId];

              if (!stage) {
                return undefined;
              }

              return stage.startDate ? fromUnixTime(stage.startDate) : undefined;
            },
          },
          {
            ...baseColumn,
            field: `stage_${stageId}_endDate`,
            headerName: t('common:endDate'),
            type: 'date',
            valueGetter: (_, row, _column, api) => {
              const id = api.current.getRowId(row);
              const theWell = store.getRecordById(id as string);

              if (!theWell) {
                console.error('cannot find corresponding record');
                return undefined;
              }

              const stagesByStageType = theWell.data['stagesByStageType'];

              if (!stagesByStageType) {
                return undefined;
              }

              const stage = stagesByStageType[stageId];

              if (!stage) {
                return undefined;
              }

              return stage.endDate ? fromUnixTime(stage.endDate) : undefined;
            },
          },
          {
            ...baseColumn,
            field: `stage_${stageId}_duration`,
            headerName: t('common:duration'),
            type: 'number',
            valueGetter: (_, row, _column, api) => {
              const id = api.current.getRowId(row);
              const theWell = store.getRecordById(id as string);

              if (!theWell) {
                console.error('cannot find corresponding record');
                return undefined;
              }

              const stagesByStageType = theWell.data['stagesByStageType'];

              if (!stagesByStageType) {
                return undefined;
              }

              const stage = stagesByStageType[stageId];

              if (!stage) {
                return undefined;
              }

              return getNumberWithDecimalPlaces(stage.duration, 2);
            },
          },
        );

        if (hasDistance) {
          stageColumns.push({
            ...baseColumn,
            field: `stage_${stageId}_distance`,
            headerName: t('common:distance'),
            type: 'number',
            valueGetter: (_, row, _column, api) => {
              const id = api.current.getRowId(row);
              const theWell = store.getRecordById(id as string);

              if (!theWell) {
                console.error('cannot find corresponding record');
                return undefined;
              }

              const stagesByStageType = theWell.data['stagesByStageType'];

              if (!stagesByStageType) {
                return undefined;
              }

              const stage = stagesByStageType[stageId];

              if (!stage) {
                return undefined;
              }

              return stage.distance ? fromUnixTime(stage.distance) : undefined;
            },
          });
        }
      });

      sectionIdsSet.forEach((sectionId) => {
        sectionColumns.push(
          {
            ...baseColumn,
            field: `section_${sectionId}_startDate`,
            headerName: t('common:startDate'),
            type: 'date',
            valueGetter: (_, row, _column, api) => {
              const id = api.current.getRowId(row);
              const theWell = store.getRecordById(id as string);

              if (!theWell) {
                console.error('cannot find corresponding record');
                return undefined;
              }

              const sectionsBySectionType = theWell.data['sectionsBySectionType'];

              if (!sectionsBySectionType) {
                return undefined;
              }

              const section = sectionsBySectionType[sectionId];

              if (!section) {
                return undefined;
              }

              return section.startDate ? fromUnixTime(section.startDate) : undefined;
            },
          },
          {
            ...baseColumn,
            field: `section_${sectionId}_endDate`,
            headerName: t('common:endDate'),
            type: 'date',
            valueGetter: (_, row, _column, api) => {
              const id = api.current.getRowId(row);
              const theWell = store.getRecordById(id as string);

              if (!theWell) {
                console.error('cannot find corresponding record');
                return undefined;
              }

              const sectionsBySectionType = theWell.data['sectionsBySectionType'];

              if (!sectionsBySectionType) {
                return undefined;
              }

              const section = sectionsBySectionType[sectionId];

              if (!section) {
                return undefined;
              }

              return section.endDate ? fromUnixTime(section.endDate) : undefined;
            },
          },
          {
            ...baseColumn,
            field: `section_${sectionId}_duration`,
            headerName: t('common:duration'),
            type: 'number',
            valueGetter: (_, row, _column, api) => {
              const id = api.current.getRowId(row);
              const theWell = store.getRecordById(id as string);

              if (!theWell) {
                console.error('cannot find corresponding record');
                return undefined;
              }

              const sectionsBySectionType = theWell.data['sectionsBySectionType'];

              if (!sectionsBySectionType) {
                return undefined;
              }

              const section = sectionsBySectionType[sectionId];

              if (!section) {
                return undefined;
              }

              return getNumberWithDecimalPlaces(section.duration, 2);
            },
          },
        );
      });

      stageColumns.push({
        ...baseColumn,
        type: 'date',
        field: 'headsFreeDate',
        headerName: t('wells:Labels.headsFreeDate'),
      });

      return { stageColumns, sectionColumns };
    })();

    const padPreparationsColumns: GridColDef<TheWellEntity>[] = [
      {
        ...baseColumn,
        minWidth: 132,
        type: 'custom',
        field: 'padReadiness',
        headerName: t('wells:Labels.padReadiness'),
        filterOperators: [statusOperator],
        sortComparator: statusComparator,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const padReadinessDate = theWell.data['padReadyDate'];

          return getReadinessStatusByDates(millisecondsToSeconds(Date.now()), padReadinessDate ?? null);
        },
        valueFormatter: (value) => {
          return t(defaultStatusData[value]);
        },
        renderCell: (params) => {
          return (
            <TableCell>
              <StatusLabel status={params.value ?? null} />
            </TableCell>
          );
        },
      },
      {
        ...baseColumn,
        type: 'date',
        field: 'padReadyDate',
        headerName: t('wells:Labels.padReadyDate'),
      },
      {
        ...baseColumn,
        type: 'date',
        field: 'mobilizationStartDate',
        headerName: t('wells:Labels.mobilizationStartDate'),
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'wellCount',
        headerName: t('wells:Labels.wellCount'),
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'unplannedWellCount',
        headerName: t('wells:Labels.unplannedWellCount'),
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'padTypeId',
        headerName: t('wells:Labels.padTypeId'),
        valueOptions: padTypeOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const padTypeId = theWell.data['padTypeId'];

          if (!hasValue(padTypeId)) {
            return null;
          }

          return padTypeOptionsMap.get(padTypeId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'equipmentLayout',
        headerName: t('wells:Labels.equipmentLayout'),
        valueFormatter: (value) => {
          return value ? i18n.t('common:available') : i18n.t('common:notAvailable');
        },
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'equipmentLayoutComment',
        headerName: t('wells:Labels.equipmentLayoutComment'),
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'engineeringSurveysCompleted',
        headerName: t('wells:Labels.engineeringSurveysCompleted'),
        valueFormatter: (value) => {
          return value ? i18n.t('common:available') : i18n.t('common:notAvailable');
        },
      },
      {
        ...baseColumn,
        type: 'number',
        field: 'engineeringSurveysComment',
        headerName: t('wells:Labels.engineeringSurveysComment'),
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'costCenter',
        headerName: t('wells:Labels.costCenter'),
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'requiredWorks',
        headerName: t('wells:Labels.requiredWorks'),
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'readinessComment',
        headerName: t('wells:Labels.readinessComment'),
      },
      {
        ...baseColumn,
        minWidth: 132,
        type: 'custom',
        field: 'padDocumentationReadiness',
        headerName: t('wells:Labels.padDocumentationReadiness'),
        filterOperators: [statusOperator],
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const forestProjectDate = theWell.data['forestProjectDate'];
          const landLeaseDate = theWell.data['landLeaseDate'];
          const currentUnixTime = millisecondsToSeconds(Date.now());

          const landReady = (() => {
            const landReady = getReadinessStatusByDates(currentUnixTime, landLeaseDate ?? null);
            const forestReady = getReadinessStatusByDates(currentUnixTime, forestProjectDate ?? null);

            const _ = [landReady, forestReady] as const;

            if (_.includes('notReady')) {
              return 'notReady';
            } else if (_.includes('inProcess')) {
              return 'inProcess';
            }

            return 'ready';
          })();

          return landReady;
        },
        valueFormatter: (value) => {
          return t(defaultStatusData[value]);
        },
        renderCell: (params) => {
          return (
            <TableCell>
              <StatusLabel status={params.value ?? null} />
            </TableCell>
          );
        },
      },
      {
        ...baseColumn,
        type: 'date',
        field: 'landLeaseDate',
        headerName: t('wells:Labels.landLeaseDate'),
      },
      {
        ...baseColumn,
        type: 'date',
        field: 'forestProjectDate',
        headerName: t('wells:Labels.forestProjectDate'),
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'documentationComment',
        headerName: t('wells:Labels.documentationComment'),
      },
      {
        ...baseColumn,
        minWidth: 132,
        type: 'custom',
        field: 'routeReadiness',
        headerName: t('wells:Labels.routeReadiness'),
        filterOperators: [statusOperator],
        sortComparator: statusComparator,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const routeReadyDate = theWell.data['routeReadyDate'];

          return getReadinessStatusByDates(millisecondsToSeconds(Date.now()), routeReadyDate ?? null);
        },
        valueFormatter: (value) => {
          return t(defaultStatusData[value]);
        },
        renderCell: (params) => {
          return (
            <TableCell>
              <StatusLabel status={params.value ?? null} />
            </TableCell>
          );
        },
      },
      {
        ...baseColumn,
        type: 'date',
        field: 'routeReadyDate',
        headerName: t('wells:Labels.routeReadyDate'),
      },
      {
        ...baseColumn,
        type: 'singleSelect',
        field: 'routeTypeId',
        headerName: t('wells:Labels.routeTypeId'),
        valueOptions: passageTypeOptions,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const passageTypeId = theWell.data['routeTypeId'];

          if (!hasValue(passageTypeId)) {
            return null;
          }

          return passageTypeOptionsMap.get(passageTypeId) ?? null;
        },
        getOptionValue: (value) => {
          return value;
        },
        getOptionLabel: (value) => {
          return typeof value === 'object' && value !== null ? value.label : value;
        },
        sortComparator: (v1, v2) => {
          const v1Label = (typeof v1 === 'object' && v1 !== null ? v1.label : v1)?.toString() ?? '';
          const v2Label = (typeof v2 === 'object' && v2 !== null ? v2.label : v2)?.toString() ?? '';

          return v1Label.localeCompare(v2Label);
        },
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'routeComment',
        headerName: t('wells:Labels.routeComment'),
      },
      {
        ...baseColumn,
        minWidth: 132,
        type: 'custom',
        field: 'routeDocumentationReadiness',
        headerName: t('wells:Labels.routeDocumentationReadiness'),
        filterOperators: [statusOperator],
        sortComparator: statusComparator,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const routeDocumentationDate = theWell.data['routeDocumentationDate'];

          return getReadinessStatusByDates(millisecondsToSeconds(Date.now()), routeDocumentationDate ?? null);
        },
        valueFormatter: (value) => {
          return t(defaultStatusData[value]);
        },
        renderCell: (params) => {
          return (
            <TableCell>
              <StatusLabel status={params.value ?? null} />
            </TableCell>
          );
        },
      },
      {
        ...baseColumn,
        type: 'date',
        field: 'routeDocumentationDate',
        headerName: t('wells:Labels.routeDocumentationDate'),
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'routeDocumentationComment',
        headerName: t('wells:Labels.routeDocumentationComment'),
      },
      {
        ...baseColumn,
        minWidth: 132,
        type: 'custom',
        field: 'electricityReadiness',
        headerName: t('wells:Labels.electricityReadiness'),
        filterOperators: [statusOperator],
        sortComparator: statusComparator,
        valueGetter: (_, row, _column, api) => {
          const id = api.current.getRowId(row);
          const theWell = store.getRecordById(id as string);

          if (!theWell) {
            console.error('cannot find corresponding record');
            return null;
          }

          const electricityDate = theWell.data['electricityDate'];

          return getReadinessStatusByDates(millisecondsToSeconds(Date.now()), electricityDate ?? null);
        },
        valueFormatter: (value) => {
          return t(defaultStatusData[value]);
        },
        renderCell: (params) => {
          return (
            <TableCell>
              <StatusLabel status={params.value ?? null} />
            </TableCell>
          );
        },
      },
      {
        ...baseColumn,
        type: 'date',
        field: 'electricityDate',
        headerName: t('wells:Labels.electricityDate'),
      },
      {
        ...baseColumn,
        type: 'string',
        field: 'electricityComment',
        headerName: t('wells:Labels.electricityComment'),
      },
    ];

    const passingColumns = (() => {
      const passingByYears = rows.reduce((acc: Record<number, { total: number; months: number[] }>, row) => {
        const { passingByMonth } = row.data;

        if (!hasValue(passingByMonth)) {
          return acc;
        }

        for (const year in passingByMonth) {
          if (!acc[year]) {
            acc[year] = { total: passingByMonth[year].total, months: [] };
          }

          for (const month in passingByMonth[year]) {
            if (month === 'total') {
              continue;
            }

            if (acc[year].months.includes(parseInt(month))) {
              continue;
            }

            acc[year].months.push(Number(month));
          }
        }

        return acc;
      }, {});

      const passingColumns: GridColDef<TheWellEntity>[] = [];

      Object.entries(passingByYears)
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .forEach(([year, { total, months }]) => {
          months
            .sort((a, b) => a - b)
            .forEach((month) => {
              passingColumns.push({
                ...baseColumn,
                type: 'number',
                field: `passing_${year}_${month}`,
                headerName: capitalize(format(new Date(Number(year), Number(month)), 'LLLL', { locale: ru })),
                valueGetter: (_, row, _column, api) => {
                  const id = api.current.getRowId(row);
                  const theWell = store.getRecordById(id as string);

                  if (!theWell) {
                    console.error('cannot find corresponding record');
                    return null;
                  }

                  return getNumberWithDecimalPlaces(theWell.data['passingByMonth']?.[Number(year)]?.[Number(month)], 0);
                },
              });
            });

          passingColumns.push({
            ...baseColumn,
            type: 'number',
            field: `passingByYear_${year}_total`,
            headerName: t('common:total'),
            valueGetter: (_, row, _column, api) => {
              const id = api.current.getRowId(row);
              const theWell = store.getRecordById(id as string);

              if (!theWell) {
                console.error('cannot find corresponding record');
                return null;
              }

              return getNumberWithDecimalPlaces(theWell.data['passingByMonth']?.[Number(year)]?.total, 0);
            },
          });

          passingColumns.push({
            ...baseColumn,
            type: 'number',
            field: `csByYear_${year}`,
            headerName: t('wells:Labels.commercialSpeed'),
            valueGetter: (_, row, _column, api) => {
              const id = api.current.getRowId(row);
              const theWell = store.getRecordById(id as string);

              if (!theWell) {
                console.error('cannot find corresponding record');
                return null;
              }

              return getNumberWithDecimalPlaces(theWell.data['csByYears']?.[Number(year)], 0);
            },
          });
        });

      return passingColumns;
    })();

    return [...columns, ...stageColumns, ...sectionColumns, ...padPreparationsColumns, ...passingColumns];
  }, [
    directories,
    blockOptions,
    fieldOptions,
    organizationOptions,
    padOptions,
    wellPurposeOptions,
    wellEndingTypeOptions,
    drillingStatusOptions,
    clusterOptions,
    fluidTypeOptions,
    wellTypeOptions,
    wellCategoryOptions,
    rigOptions,
    brigadeOptions,
    padTypeOptions,
    passageTypeOptions,
    reservoirOptions,
    coreSamplingIntervalOptions,
    baseColumn,
    t,
    store,
    getClusterIdFn,
    rows,
    directoriesObjects.wellLifeCycleStages,
  ]);
};
